import * as Actions from './entities.actions';
import { createLookupHash } from '../../services/hash-service';
import { FulfillmentPlanFulfillmentType } from '../../enums/fulfillment-plan-fulfillment-type';

const urlMap = [
  /^\/ordergrid\/shipmentmode$/i,
  /^\/ordergrid\/shipmentmode\/simple$/i,
  /^ordergrid\/shipmentmode\/quicksearch$/i,
  /^\/ordergrid\/shipmentmode\/quicksearch$/i,
  /^\/ordergrid\/shipmentmode\/advancedsearch/i,
  /^\/shippinggrid\/simple$/i,
  /^\/fulfillmentGrid/i,
  /^\/bulkload\/.*$/i,
  /^\/order\/search\/fulfillmentPlanIds/i,
  /^\/fulfillmentplan\/saveconfigandrate2\/[0-9]+$/i,
];

const mergeShipToWithStateDefault = (labelConfiguration, externalFulfillmentConfiguration) => {
  if (externalFulfillmentConfiguration) {
    return {
      ...externalFulfillmentConfiguration.shipTo,
      state: externalFulfillmentConfiguration.shipTo?.state || '',
    };
  }
  if (labelConfiguration) {
    return {
      ...labelConfiguration.shipTo,
      state: labelConfiguration.shipTo?.state || '',
    };
  }
  return {};
};

export const checkIfResponseContainsBulkLoadProperties = (responseData) => {
  const { fulfillmentPlans, fulfillmentPlanSets, fulfillments, salesOrders, returns } = responseData;

  return Boolean(fulfillmentPlans?.length || fulfillmentPlanSets?.length || fulfillments?.length || salesOrders?.length || returns?.length);
};

export const processEntities = (responseData) => {
  const now = new Date();

  const {
    fulfillmentPlans: responseFulfillmentPlans,
    fulfillmentPlanSets: responseFulfillmentPlanSets,
    fulfillments: responseFulfillments,
    salesOrders: responseSalesOrders,
    returns: responseReturns,
    pickBatches: responsePickBatches,
  } = responseData;

  const fulfillmentPlans = responseFulfillmentPlans || [];
  const fulfillmentPlanSets = responseFulfillmentPlanSets || [];
  const fulfillments = responseFulfillments || [];
  const salesOrders = responseSalesOrders || [];
  const returns = responseReturns || [];

  const adjustedFulfillmentPlans = fulfillmentPlans.map((fulfillmentPlan) => {
    const fulfillmentIds = fulfillments
      .filter((f) => f.fulfillmentPlanId === fulfillmentPlan.fulfillmentPlanId)
      .map((f) => f.fulfillmentId);
    return {
      ...fulfillmentPlan,
      ...fulfillmentPlan.labelConfiguration,
      ...fulfillmentPlan.externalFulfillmentConfiguration,
      shipTo: mergeShipToWithStateDefault(fulfillmentPlan.labelConfiguration, fulfillmentPlan.externalFulfillmentConfiguration),
      properties: fulfillmentPlan.international,
      lastLoadedDateTime: now,
      fulfillmentIds,
      salesOrdersFulfilledIds: fulfillmentPlan.salesOrdersFulfilledIds || [],
    };
  });

  const adjustedFulfillments = fulfillments.map((fulfillment) => {
    const firstPackage = (fulfillment.packages && fulfillment.packages[0]) || {};
    const isLabelFulfillmentType = fulfillment.fulfillmentType === FulfillmentPlanFulfillmentType.Shipment;
    const fulfillmentConfig = isLabelFulfillmentType ? fulfillment.labelFulfillment : fulfillment.externalFulfillment;
    return {
      ...fulfillment,
      ...fulfillmentConfig,
      ...firstPackage,
      lastLoadedDateTime: now,
      properties: {
        hasCustomsForm: fulfillment.labelFulfillment?.shouldHaveCustomsForm || false,
        // eslint-disable-next-line
        hasFormFile: fulfillment.labelFulfillment?.hasCustomsFormFile != null,
      },
    };
  });

  const adjustedSalesOrders = salesOrders.map((salesOrder) => {
    const { salesOrderId, fulfillmentPlanIds: backendFulfillmentPlanIds } = salesOrder;
    const fulfillmentPlanIds =
      fulfillmentPlans
        .filter((fulfillmentPlan) => fulfillmentPlan.salesOrdersFulfilledIds.includes(salesOrderId))
        .map((fp) => fp.fulfillmentPlanId) || [];
    const fulfillmentIds = fulfillments
      .filter((fulfillment) => fulfillmentPlanIds.includes(fulfillment.fulfillmentPlanId))
      .map((f) => f.fulfillmentId);
    const returnIds = (returns || []).filter((ret) => ret.salesOrderId === salesOrderId).map((f) => f.fulfillmentId);
    const otherSalesOrderIdsFulfilled = [
      ...new Set(
        fulfillmentPlans.reduce((acc, fulfillmentPlan) => {
          if (!fulfillmentPlanIds.includes(fulfillmentPlan.fulfillmentPlanIds)) {
            return acc;
          }

          return {
            ...acc,
            ...fulfillmentPlan.salesOrdersFulfilledIds.filter((s) => s !== salesOrderId),
          };
        }, []),
      ),
    ];
    const shipTos =
      salesOrder.shipTos?.map((shipTo) => ({
        ...shipTo,
        state: shipTo?.state || '',
      })) || [];

    return {
      ...salesOrder,
      fulfillmentPlanIds: backendFulfillmentPlanIds ?? fulfillmentPlanIds,
      fulfillmentIds,
      returnIds,
      otherSalesOrderIdsFulfilled,
      lastLoadedDateTime: now,
      shipTos,
    };
  });

  return {
    fulfillmentPlans: createLookupHash(adjustedFulfillmentPlans || [], 'fulfillmentPlanId'),
    fulfillmentPlanSets: createLookupHash(fulfillmentPlanSets || [], 'fulfillmentPlanSetId'),
    fulfillments: createLookupHash(adjustedFulfillments || [], 'fulfillmentId'),
    salesOrders: createLookupHash(adjustedSalesOrders || [], 'salesOrderId'),
    returns: createLookupHash(returns || [], 'fulfillmentId'),
    pickBatches: createLookupHash(responsePickBatches || [], 'pickBatchId'),
  };
};

const bulkload = (url = '', responseData, dispatch) => {
  const matchingUrls = urlMap.filter((i) => url.match(i));

  if (matchingUrls.length === 0) {
    return;
  }

  dispatch(Actions.entitiesBulkload(processEntities(responseData)));
};

export default bulkload;
